/**
 * EditableImage (web) — Renders an image (logo, hero, etc.). If the current
 * user is the owner, shows a camera badge on hover. Clicking it opens a
 * file picker, uploads the new image to /api/branding/upload, and the
 * change is live everywhere for every user.
 *
 * Non-owners see only the image with no affordance. The owner can swap
 * the image from any page at any time.
 */

import React, { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";

export default function EditableImage({
  brandKey,
  alt = "",
  className = "",
  imgClassName = "",
  fallback = null,
  testId,
  badgePosition = "bottom-right", // bottom-right | top-right
}) {
  const { user } = useAuth();
  const { get, resolveImage, uploadImage } = useBranding();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const urlOrPath = get(brandKey, "");
  const absUrl = urlOrPath ? resolveImage(urlOrPath) : "";
  const isOwner = !!user?.is_owner;

  const onPickFile = () => {
    inputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please pick an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image too large (max 5MB).");
      return;
    }
    setUploading(true);
    const res = await uploadImage(brandKey, file);
    setUploading(false);
    if (!res.ok) {
      alert(`Upload failed: ${res.error}`);
    }
    // reset input so same file can be picked again next time
    if (inputRef.current) inputRef.current.value = "";
  };

  const badgeClass =
    badgePosition === "top-right"
      ? "top-0 right-0 translate-x-1/3 -translate-y-1/3"
      : "bottom-0 right-0 translate-x-1/3 translate-y-1/3";

  return (
    <span className={`relative inline-flex group ${className}`} data-testid={testId}>
      {absUrl ? (
        <img src={absUrl} alt={alt} className={imgClassName} />
      ) : (
        fallback
      )}

      {isOwner && (
        <>
          <button
            type="button"
            onClick={onPickFile}
            disabled={uploading}
            className={`absolute ${badgeClass} opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FF3008] text-white shadow-lg border-2 border-white hover:scale-110`}
            aria-label={`Change ${brandKey}`}
            data-testid={`edit-${brandKey}-camera`}
          >
            {uploading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Camera size={14} strokeWidth={2.2} />
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onFileSelected}
            className="hidden"
            data-testid={`edit-${brandKey}-input`}
          />
        </>
      )}
    </span>
  );
}
